import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useT } from '../lib/i18n/I18nContext';
import { styles, Logo } from './Login';

/**
 * ResetPassword — completes the "Forgot password?" flow.
 *
 * Supabase emails a recovery link (redirectTo `${origin}/reset-password`). The
 * client (detectSessionInUrl: true) processes the URL and fires PASSWORD_RECOVERY,
 * establishing a short-lived recovery session. This page collects a new password and
 * calls `supabase.auth.updateUser({ password })`, then signs out so the next login uses
 * the new credential. Rendered by main.jsx AuthGate when the URL is a recovery URL,
 * BEFORE the normal login/app gate (a recovery session would otherwise drop the CEO
 * straight into the app without ever setting a new password).
 *
 * Before this existed the flow dead-ended: Login called resetPasswordForEmail but there
 * was no page to complete it (no updateUser anywhere), so no user could actually reset.
 */
export default function ResetPassword() {
  const { t } = useT();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [focused, setFocused] = useState(null);

  // The recovery session is established asynchronously as supabase-js parses the URL.
  // We don't gate the form on it (updateUser surfaces a clear error if it's missing),
  // but we clean the token out of the address bar once it's processed.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' && window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    });
    return () => subscription?.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError(t('auth.pwTooShort')); return; }
    if (password !== confirm) { setError(t('auth.pwMismatch')); return; }
    setSubmitting(true);
    setError(null);

    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) {
      // "Auth session missing" => the recovery link expired or was already used.
      setError(/session|missing|expired/i.test(err.message || '') ? t('auth.resetLinkInvalid') : (err.message || t('auth.genericError')));
      setSubmitting(false);
      return;
    }

    // Success: drop the recovery session so the next sign-in uses the new password.
    setDone(true);
    setSubmitting(false);
    try { await supabase.auth.signOut(); } catch { /* best-effort; redirect anyway */ }
    setTimeout(() => { window.location.assign('/'); }, 1800);
  };

  const disabled = submitting || done;

  if (done) {
    return (
      <div style={styles.page}>
        <div style={styles.card} data-testid="reset-password-done">
          <div style={styles.logoWrap}>
            <Logo size={48} />
            <h1 style={styles.title}>{t('auth.pwUpdated')}</h1>
          </div>
          <p style={{ ...styles.subtitle, textAlign: 'center', lineHeight: 1.5 }}>{t('auth.pwUpdatedBody')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoWrap}>
          <Logo size={56} />
          <h1 style={styles.title}>{t('auth.resetTitle')}</h1>
          <p style={styles.subtitle}>{t('auth.resetSubtitle')}</p>
        </div>

        {error && <p style={styles.error} data-testid="reset-password-error">{error}</p>}

        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('auth.newPassword')}</label>
            <input
              type="password"
              data-testid="reset-password-new"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocused('pw')}
              onBlur={() => setFocused(null)}
              placeholder={t('auth.passwordPlaceholder')}
              autoComplete="new-password"
              disabled={disabled}
              style={{ ...styles.input, ...(focused === 'pw' ? styles.inputFocus : {}) }}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('auth.confirmPassword')}</label>
            <input
              type="password"
              data-testid="reset-password-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onFocus={() => setFocused('confirm')}
              onBlur={() => setFocused(null)}
              placeholder={t('auth.passwordPlaceholder')}
              autoComplete="new-password"
              disabled={disabled}
              style={{ ...styles.input, ...(focused === 'confirm' ? styles.inputFocus : {}) }}
            />
          </div>

          <button
            type="submit"
            data-testid="reset-password-submit"
            disabled={disabled || !password || !confirm}
            style={{ ...styles.button, ...(disabled || !password || !confirm ? styles.buttonDisabled : {}) }}
          >
            {submitting ? t('auth.updating') : t('auth.updatePassword')}
          </button>
        </form>

        <button
          style={styles.forgotLink}
          data-testid="reset-password-back"
          onClick={() => window.location.assign('/')}
          disabled={submitting}
        >
          {t('auth.backToLogin')}
        </button>
      </div>
    </div>
  );
}
